import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Button from '../../components/common/Button';
import { adminsApi } from '../../api/admins.api';
import { rolesApi } from '../../api/roles.api';
import { useAuth } from '../../context/AuthContext';

export default function AdminFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [targetAdminRole, setTargetAdminRole] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    isActive: true
  });

  // Determine if current user can directly reset password for target admin
  const canResetPasswordDirectly = () => {
    if (!isEdit) return true; // New admin creation
    if (isSuperAdmin) return true; // Superadmins can reset any password
    if (targetAdminRole === 'basic') return true; // Admins can reset basic users' passwords
    return false;
  };

  useEffect(() => {
    // Fetch roles
    rolesApi.getAll().then((res) => {
      const fetchedRoles = res.data.data || [];
      setAllRoles(fetchedRoles);

      let availableRoles = fetchedRoles;

      // If not superadmin, filter out superadmin role
      if (!isSuperAdmin) {
        availableRoles = availableRoles.filter(r => r.name !== 'superadmin');
      }

      setRoles(availableRoles.map((r) => ({ value: r._id, label: r.name })));
    });

    if (isEdit) {
      setLoading(true);
      adminsApi.getById(id).then((res) => {
        const admin = res.data;
        setFormData({
          name: admin.name || '',
          email: admin.email || '',
          password: '',
          role: admin.role?._id || '',
          isActive: admin.isActive ?? true
        });
        setTargetAdminRole(admin.role?.name || null);
        setLoading(false);
      }).catch(() => {
        setError('Failed to load admin');
        setLoading(false);
      });
    }
  }, [id, isEdit, isSuperAdmin]);

  // Update target role when role selection changes
  useEffect(() => {
    if (formData.role && allRoles.length > 0) {
      const selectedRole = allRoles.find(r => r._id === formData.role);
      if (selectedRole) {
        setTargetAdminRole(selectedRole.name);
      }
    }
  }, [formData.role, allRoles]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const dataToSend = { ...formData };

      // Don't send empty password on edit
      if (isEdit && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (isEdit) {
        await adminsApi.update(id, dataToSend);
      } else {
        await adminsApi.create(dataToSend);
      }
      navigate('/admins');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save admin');
    } finally {
      setSaving(false);
    }
  };

  const handleSendResetEmail = async () => {
    setError('');
    setSuccess('');
    setSendingResetEmail(true);

    try {
      const response = await adminsApi.sendResetEmail(id);
      setSuccess(response.data.message || 'Password reset email sent successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email');
    } finally {
      setSendingResetEmail(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Admin' : 'Create Admin'}
        </h1>
        {!isSuperAdmin && (
          <p className="mt-1 text-sm text-gray-500">
            As an admin, you can only assign admin or basic roles.
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <Select
              label="Role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              options={roles}
              required
            />
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
          </div>

          {/* Password Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Password</h3>

            {canResetPasswordDirectly() ? (
              <div className="max-w-md">
                <Input
                  label={isEdit ? 'New Password (leave blank to keep current)' : 'Password'}
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEdit}
                />
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  You cannot directly reset passwords for admin or superadmin users.
                  Use the button below to send a password reset email.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSendResetEmail}
                  loading={sendingResetEmail}
                >
                  Send Password Reset Email
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => navigate('/admins')}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {isEdit ? 'Update Admin' : 'Create Admin'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
